import React, { useEffect, useRef, useState } from 'react';
import { USER_STATUS_OPTIONS, USER_STATUS_DISPLAY_DEFAULT } from '../../types/sift';

// "Unassigned" is the absence of a status row rather than a stored value, so it is
// not in USER_STATUS_OPTIONS (that list is what a user can ASSIGN). It is still a
// real slice of the board — untouched work — so it leads the filter list.
const STATUS_FILTER_OPTIONS: string[] = [
  USER_STATUS_DISPLAY_DEFAULT,
  ...USER_STATUS_OPTIONS.filter((s): s is Exclude<typeof s, null> => Boolean(s)),
];

interface Props {
  selected: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}

/**
 * Filter the carousel to dealers holding at least one VIN in the chosen statuses.
 *
 * Applied SERVER-side (a `user_status` param), unlike the priority chips which
 * filter client-side off a per-point counts array. Shipping a per-seller status
 * pivot would have added ~6 MB to a map-point payload that is already ~40 MB for
 * the unfiltered 139k-dealer set.
 */
export const VinStatusFilterPopover: React.FC<Props> = ({ selected, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggle = (status: string) => {
    onChange(selected.includes(status)
      ? selected.filter((s) => s !== status)
      : [...selected, status]);
  };

  return (
    <div className="cv-metric-filters" ref={wrapRef}>
      <button
        type="button"
        className={`cv-metric-filters-btn${selected.length > 0 ? ' active' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        title="Show only dealers holding a VIN in these statuses"
      >
        Status{selected.length > 0 ? ` (${selected.length})` : ''} ▾
      </button>
      {open && (
        <div className="cv-metric-filters-menu" role="dialog" aria-label="VIN status filter">
          <div className="cv-metric-filters-head">
            <span>VIN status</span>
            {selected.length > 0 && (
              <button type="button" className="cv-metric-filters-clear" onClick={() => onChange([])}>
                Clear all
              </button>
            )}
          </div>
          <div className="cv-status-filter-rows">
            {STATUS_FILTER_OPTIONS.map((status) => (
              <label key={status} className="cv-status-filter-row">
                <input
                  type="checkbox"
                  checked={selected.includes(status)}
                  onChange={() => toggle(status)}
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
          <p className="cv-status-filter-note">
            Keeps dealers with at least one VIN in the selected statuses.
          </p>
        </div>
      )}
    </div>
  );
};
